import SERVICES_API_V1 from 'api/services/v1'
import SERVICES_API_V2 from 'api/services/v2'
import YANDEX_API_V1 from 'api/yandex/v1'
import { startSubmit, stopSubmit, SubmissionError } from 'redux-form'
import { call, put, takeLatest } from 'redux-saga/effects'
import extractErrors from 'utils/extractErrors'
import omit from 'lodash/omit'
import { notificationShow } from 'modules/Notifications/actions'
import i18next from 'i18next'
import {
  ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
  CONFIRM_EXIT_FROM_CREATE_ACCOUNT,
  CREATE_MULTIPLE_ACCOUNTS,
  CREATE_NEW_ACCOUNT,
  FETCH_NETWORK_PAYMENTS_ACCOUNTS,
  FETCH_NETWORK_SETTINGS,
  FETCH_YANDEX_USERS,
  SET_ACCOUNT_CONNECT_MANUAL,
  SET_NETWORK_PAYMENT_ACCOUNTS,
  SET_NETWORK_SETTINGS,
  SET_YANDEX_USERS,
} from 'modules/Services/types'
import { confirmSaga } from 'store/saga-utils/confirm.saga'

function* fetchNetworkPaymentsAccount({ payload }) {
  try {
    const { response, error } = yield call(SERVICES_API_V1.getNetworkPaymentAccounts, payload)

    if (error) {
      throw error
    }

    yield put({
      type: SET_NETWORK_PAYMENT_ACCOUNTS,
      payload: response.data,
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'services::forms.shared_fields.payment_profile_id.errors.fetch_error',
      }),
    )
    console.logXHR(error)
  }
}

function* fetchNetworkSettings({ payload }) {
  try {
    const { response, error } = yield call(SERVICES_API_V1.getNetworkSettings, payload)

    if (error) {
      throw error
    }

    yield put({
      type: SET_NETWORK_SETTINGS,
      payload: response.data,
    })
  } catch (error) {
    yield put({
      type: SET_NETWORK_SETTINGS,
      payload: null,
    })
    console.logXHR(error)
  }
}

function* createNewAccount({
  payload: { data, formName },
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    yield put(startSubmit(formName))
    console.log('data', data)

    const { error } = yield call(SERVICES_API_V2.addNetworkAccount, {
      data,
    })

    yield put(stopSubmit(formName))

    if (error) {
      throw error
    }

    yield put(
      notificationShow({
        type: 'success',
        message: 'common::notifications.data_success_add',
      }),
    )

    resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    yield put(stopSubmit(formName, fields))

    reject(fields ? new SubmissionError(fields) : 'Unexpected form error')

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
          delay: 10000,
        }),
      )
    }
  }
}

function* ConnectManualLoad({ payload }) {
  try {
    yield put({
      type: SET_ACCOUNT_CONNECT_MANUAL,
      payload: { connectManualLoading: true, connectManualText: {} },
    })

    const { response, error } = yield call(SERVICES_API_V1.getNetworkText, {
      ...payload,
      textKey: 'connect',
    })

    if (error || !response.data) {
      throw new Error('service manual loading failed ( connect )')
    }

    yield put({
      type: SET_ACCOUNT_CONNECT_MANUAL,
      payload: {
        connectManualText: response.data,
        connectManualLoading: false,
      },
    })
  } catch (error) {
    yield put({
      type: SET_ACCOUNT_CONNECT_MANUAL,
      payload: { connectManualLoading: false, connectManualText: {} },
    })
    console.logXHR(error)
  }
}

function* createMultiAccounts({
  payload: { data, formName },
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  const { logins } = data

  const results = []

  try {
    yield put(startSubmit(formName))

    yield (async () => {
      for (let index = 0; index < data.logins.length; index++) {
        const login = data.logins[index].login

        const resp = await SERVICES_API_V2.addNetworkAccount({
          data: {
            ...omit(data, ['logins']),
            login,
          },
        })

        results.push(resp)
      }
    })()

    yield put(stopSubmit(formName))

    const { success, errors } = results.reduce(
      (acc, response, i) => {
        if (response.error && response.error?.errors) {
          acc.errors = acc.errors.concat({ index: i, error: response.error })
        } else {
          acc.success = acc.success.concat({
            index: i,
            data: response.data,
          })
        }

        return acc
      },
      {
        success: [],
        errors: [],
      },
    )

    const accountStates = {
      ...success.reduce(
        (acc, { index }) => ({
          ...acc,
          [logins[index].index]: { created: true },
        }),
        {},
      ),
      ...errors.reduce(
        (acc, { index }) => ({
          ...acc,
          [logins[index].index]: { created: false },
        }),
        {},
      ),
    }

    let action = {}

    switch (true) {
      case errors.length === 0:
        action = {
          type: 'success',
          message: i18next.t(
            'services::forms.register_account.notifications.multiple_logins_create.all_success',
          ),
        }
        break
      case errors.length > 0 && success.length > 0:
        action = {
          type: 'warning',
          message: i18next.t(
            'services::forms.register_account.notifications.multiple_logins_create.partially_success',
          ),
        }
        break
      case success.length === 0:
        action = {
          type: 'error',
          message: i18next.t(
            'services::forms.register_account.notifications.multiple_logins_create.all_failed',
          ),
        }
        break
    }

    yield put(notificationShow(action))

    if (errors.length > 0) {
      throw { accountStates, errors }
    }

    resolve(accountStates)
  } catch ({ accountStates, errors }) {
    const { form, fields } = errors.reduce(
      (acc, { error, index }) => {
        const { form, fields } = extractErrors(error.errors)

        //Безумная трансформация трех ошибок от трех отправок ТИПА трех форм в ошибку для одной формы с полем-массивом logins
        if (fields.login) {
          acc.fields.logins = acc.fields?.logins || []
          acc.fields.logins[logins[index].index] = fields.login
          delete fields.login
        }

        return { form, fields: { ...acc.fields, ...fields } }
      },
      { form: '', fields: {} },
    )

    yield put(stopSubmit(formName, fields))

    reject({
      accountStates,
      error: fields ? new SubmissionError(fields) : 'Unexpected form error',
    })

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: form || 'Unexpected form error',
          delay: 10000,
        }),
      )
    }
  }
}

function* fetchYandexUsers() {
  try {
    yield put({
      type: SET_YANDEX_USERS,
      payload: { loading: true, loaded: false, failed: false },
    })

    const { response, error } = yield call(YANDEX_API_V1.fetchUsers)

    if (error) {
      throw error
    }

    yield put({
      type: SET_YANDEX_USERS,
      payload: { data: response.data, loaded: true },
    })
  } catch (error) {
    console.logXHR(error)
    yield put({
      type: SET_YANDEX_USERS,
      payload: { failed: true },
    })
  } finally {
    yield put({
      type: SET_YANDEX_USERS,
      payload: { loading: false },
    })
  }
}

function* confirmExitFromCreateAccount({ payload }) {
  const { meta: { resolve = () => {}, reject = () => {} } = {}, ...rest } = payload
  try {
    const [confirmed, UUID] = yield call(confirmSaga, {
      modalType: 'confirmation',
      ...rest,
    })

    if (confirmed) {
      yield put({ type: 'MODAL/HIDE', payload: { UUID } })
      resolve()
    } else {
      yield put({ type: 'MODAL/HIDE', payload: { UUID } })
      reject()
    }
  } catch (error) {
    console.logXHR(error)
  }
}

export default function* Sagas() {
  yield takeLatest(FETCH_NETWORK_PAYMENTS_ACCOUNTS, fetchNetworkPaymentsAccount)
  yield takeLatest(FETCH_NETWORK_SETTINGS, fetchNetworkSettings)
  yield takeLatest(CREATE_NEW_ACCOUNT, createNewAccount)
  yield takeLatest(ACCOUNT_CREATE_CONNECT_MANUAL_LOAD, ConnectManualLoad)
  yield takeLatest(CREATE_MULTIPLE_ACCOUNTS, createMultiAccounts)
  yield takeLatest(FETCH_YANDEX_USERS, fetchYandexUsers)
  yield takeLatest(CONFIRM_EXIT_FROM_CREATE_ACCOUNT, confirmExitFromCreateAccount)
}
